import { Injectable } from '@angular/core';
import { Subject ,  BehaviorSubject } from 'rxjs';

import { Product, TwoFactorInfo } from '../shared/models';
import { CRMItem } from '../shared/models/crm/CRMItem.model';
import { CrmItemType } from '../shared/enums/crm-item-type.enum';
import { StructCapCRMItem } from '../shared/models/crm/struct-cap/StructCapCRMItem.model';

interface CRMData  {
    inputCrmItem?: CRMItem | StructCapCRMItem;
    editMode?: boolean;
    inputCompanyId?: number;
    newRecordWarning?: boolean;
    crmItemType?: CrmItemType;
    onSave?: (res: any) => void;
    onCancel?: (res: any) => void;
    onErr?: (res: any) => void;
  }

@Injectable({
  providedIn: 'root'
})
export class EventsHubService {
  public isLoggedIn$ = new Subject<boolean>();
  public httpUnauthorizedError$ = new Subject<boolean>();
  public isMenuOpen$ = new BehaviorSubject<boolean>(false);
  public profileUpdated$ = new Subject<boolean>();
  public openModal$ = new Subject<any>();
  public reloadRecords$ =  new Subject<any>();
  public passwordExpired$ =  new BehaviorSubject<any>({});
  public recordAffected$ = new Subject<boolean>();
  public onAddCompanyModalOpens$ = new Subject<boolean>();
  public onAddPortfolioModalOpens$ = new Subject<boolean>();
  public onAddProductModalOpens$ = new Subject<Product>();
  public onAddCrmModalOpens$ = new Subject<CRMData>();
  public onCrmItemCreated$ = new Subject<any>();
  public onCrmItemAgendaSent$ = new Subject<any>();
  public resetQuarterPicker$ = new Subject<boolean>();
  public productEdited$ = new Subject<null>();
  public dashboardTimerCompletes$ = new Subject<null>();
  public toggleProdNotes$ = new Subject<null>();
  public toggleIndicationNotes$ = new Subject<null>();
  public toggleConferenceNotes$ = new Subject<null>();
  public getProdCount$ = new Subject<number>();
  public getConferenceRefNotesCount$ = new Subject<number>();
  public getIndicationNotesCount$ = new Subject<number>();
  public duoSecureError$ = new BehaviorSubject<TwoFactorInfo>({
    username: '',
    password: '',
    authCode: '',
    twoFactor: true
  });
  public stopHeartBeat$ = new Subject<null>();

  constructor() {
  }

  public setLoggedIn(value: boolean) {
    this.isLoggedIn$.next(value);
  }

  public setUnauthorizedError(value: boolean) {
    this.httpUnauthorizedError$.next(value);
  }

  public setMenuState(value: boolean) {
    this.isMenuOpen$.next(value);
  }

  public profileUpdated(value: boolean) {
    this.profileUpdated$.next(value);
  }

  /**
   * Subject to detect when form modal is open
   * @param {T} data: type of data to pass in
   */
  public openModal<T> (data?: T) {
    this.openModal$.next(data);
  }

  public reloadRecords () {
    this.reloadRecords$.next(undefined);
  }

  public duoSecureError(value: TwoFactorInfo) {
    this.duoSecureError$.next(value);
  }

  public passwordExpired(value: any) {
    this.passwordExpired$.next(value);
  }

  /**
   * Event to notify when a component updates a record an notify other subscribed components
   */
  public recordAffected () {
    this.recordAffected$.next(true);
  }

  /**
   * Event to open the create new company modal
   */
  public openAddCompanyModal (redirect: boolean = true) {
    this.onAddCompanyModalOpens$.next(redirect);
  }

  /**
   * Event to open the create new portfolio modal
   */
  public openAddPortfolioModal () {
    this.onAddPortfolioModalOpens$.next(true);
  }

  /**
   * Event to open the create new company modal
   */
  public openAddProductModal (product?: Product) {
    this.onAddProductModalOpens$.next(product);
  }

  /**
   * Resets quarter selection form quarter picker component
   */
  public resetQuarterPicker () {
    this.resetQuarterPicker$.next(true);
  }

  /**
   * Dispatch an event on product basic infor is edited
   */
  public productEdited () {
    this.productEdited$.next(undefined);
  }

  /**
   * On Fund summary counter ends, emits and event to be catch by the performance dashboard chart and refreshes the data
   */
  public dashboardTimerCompletes () {
    this.dashboardTimerCompletes$.next(undefined);
  }

  /**
   * Toggle display Product Notes
   */
  public toggleProdNotes (): void {
    this.toggleProdNotes$.next(undefined);
  }

  /**
   * Toggle display Indication Notes
   */
  public toggleIndicationNotes (): void {
    this.toggleIndicationNotes$.next(undefined);
  }

  /**
   * Toggle display Conferences Notes
   */
  public toggleConferenceNotes (): void {
    this.toggleConferenceNotes$.next(undefined);
  }

  /**
   * Gets the total record from the product notes component and passes to the product parent component
   * @param value
   */
  public getProdCount (value: number): void {
    this.getProdCount$.next(value);
  }

  /**
   * Gets the total record from the conference notes component and passes to the conference parent component
   * @param value
   */
  public getConferenceRefNotesCount (value: number): void {
    this.getConferenceRefNotesCount$.next(value);
  }



  /**
   * Gets the total record from the indication notes component and passes to the indication detail parent component
   * @param value
   */
  public getIndicationNotesCount (value: number): void {
    this.getIndicationNotesCount$.next(value);
  }

  /**
   * Emits whenever heartbeat must be stopped.
   * @returns void
   */
  public stopHeartBeat (): void {
    this.stopHeartBeat$.next(undefined);
  }

  /**
   * Event to open the create new crm modal
   */
  public openAddCrmModal(data: CRMData): void {
    this.onAddCrmModalOpens$.next(data);
  }

  /**
   * Event to fire when a new CRM item is created
   */
  public crmItemCreated(data: CRMData): void {
    this.onCrmItemCreated$.next(data);
  }

  /**
   * Event to fire when a CRM Agenda is being sent
   */
    public openSendAgendaModal(data): void {
      this.onCrmItemAgendaSent$.next(data);
    }
}
